/**
 * Refined Design — the builds ribbon, once a hand touches it.
 *
 * The ribbon is a CSS animation: the track carries two identical halves and slides by
 * exactly one of them, forever, with no script involved. That is how it arrives and
 * how it stays if this file never loads — and a CSS transform is something you can
 * only watch, not push.
 *
 * So the first swipe, drag or trackpad flick hands the ribbon over. The animation is
 * frozen where it stands and its offset is handed to the scroll position, which looks
 * like nothing happened, and from there the row is an ordinary sideways scroller: a
 * finger swipes it with the browser's own momentum, a mouse drags it. The drift is
 * picked up again in JavaScript, a frame at a time, so a ribbon that has been pushed
 * keeps moving the way it did before it was touched, and it still holds still under a
 * pointer or a focused card.
 *
 * Each half is identical, so the scroll can be thrown back a lap whenever it reaches
 * the seam, in either direction. Nothing about the picture changes at that moment:
 * that is what makes the ribbon endless in the hand rather than just to the eye.
 *
 * Reduced motion keeps the still, swipeable row the stylesheet already builds — the
 * repeats are hidden there, so there is no lap to loop and no drift to resume.
 */

class RdBuildsMarquee extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.track = this.querySelector('.rd-builds__track');
    if (!this.track) return;
    this.dataset.ready = '1';

    // Nothing can be scrolled until the script that keeps it looping is here: without
    // this class the ribbon is the animation alone, and a scroll would run past the
    // end of a track that is halfway through sliding out from under it.
    this.classList.add('is-scrollable');
    this.manual = false;
    this.dragging = false;

    this.still = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.detachDrag = window.RdDragScroll
      ? window.RdDragScroll.attach(this, { onStart: this.onDragStart, onEnd: this.onDragEnd })
      : null;

    if (this.still.matches) return;

    // The handover is taken at the touch rather than at the first pixel of movement:
    // freezing the animation mid-swipe would shift the scroll position under a finger
    // that is already moving it, and that is the one moment it would be felt.
    //
    // But only a touch that turns out to be a push. A press is held still where it
    // lands — the animation paused, nothing moved — and handed over once it travels.
    // Taking over on the press itself rewrote the scroll position under a finger that
    // was only tapping a card, and a phone drops the tap when the page scrolls beneath
    // it: the ribbon stopped and the card never opened.
    this.addEventListener('pointerdown', this.onPress);
    this.addEventListener('pointerup', this.onRelease);
    this.addEventListener('pointercancel', this.onRelease);
    this.addEventListener('touchmove', this.onReach, { passive: true });
    this.addEventListener('wheel', this.onReach, { passive: true });
    // A keyboard or a scrollbar reaches the row without either, so the scroll itself
    // is still a handover of last resort.
    this.addEventListener('scroll', this.onScroll, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onPress);
    this.removeEventListener('pointerup', this.onRelease);
    this.removeEventListener('pointercancel', this.onRelease);
    this.removeEventListener('touchmove', this.onReach);
    this.removeEventListener('wheel', this.onReach);
    this.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();
    if (this.detachDrag) this.detachDrag();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.dataset.ready = '';
  }

  onReach = () => this.takeOver();

  /** A press holds the ribbon where it is, and changes nothing else. */
  onPress = () => {
    if (this.manual) return;
    this.pressed = true;
    this.track.style.animationPlayState = 'paused';
  };

  /** Let go without having pushed it: it was a tap, and the ribbon drifts on. */
  onRelease = () => {
    if (!this.pressed) return;
    this.pressed = false;
    if (!this.manual) this.track.style.animationPlayState = '';
  };

  onDragStart = () => {
    this.dragging = true;
    this.takeOver();
  };

  onDragEnd = () => {
    this.dragging = false;
  };

  onScroll = () => {
    if (!this.manual) return this.takeOver();
    this.lap();
  };

  /** Half a track: one full set of the builds, which is the length of a lap. */
  half() {
    return this.scrollWidth / 2;
  }

  /** How far the animation has slid the track by now, as a positive distance. */
  offset() {
    const transform = window.getComputedStyle(this.track).transform;
    const numbers = transform && transform.match(/\(([^)]+)\)/);
    if (!numbers) return 0;
    const parts = numbers[1].split(',').map(Number);
    const x = parts.length > 6 ? parts[12] : parts[4];
    return Number.isFinite(x) ? -x : 0;
  }

  /** The animation stops and the scroll position carries on from where it left it. */
  takeOver() {
    if (this.manual) return;
    this.manual = true;
    this.pressed = false;
    const slid = this.offset();
    this.track.style.animationPlayState = '';
    this.track.style.animation = 'none';
    this.track.style.transform = 'none';
    this.scrollLeft += slid;
    this.measure();
    this.last = 0;
    this.frame = requestAnimationFrame(this.tick);
  }

  /** The same lap in the same number of seconds the stylesheet was giving it. */
  measure() {
    const seconds = parseFloat(window.getComputedStyle(this).getPropertyValue('--rd-loop')) || 60;
    this.speed = this.half() / seconds;
  }

  /** Past the seam in either direction, back a lap onto the identical frame. */
  lap() {
    const half = this.half();
    if (half < 1) return;
    if (this.scrollLeft >= half) this.scrollLeft -= half;
    else if (this.scrollLeft <= 0) this.scrollLeft += half;
  }

  /** Hover, a focused card and a hand on the ribbon all hold it still, as the CSS did. */
  held() {
    return this.dragging || this.matches(':hover') || this.contains(document.activeElement);
  }

  tick = (now) => {
    this.frame = requestAnimationFrame(this.tick);
    const previous = this.last || now;
    this.last = now;
    if (this.held()) return;
    // A frame dropped to a background tab or a slow paint should not fling the ribbon
    // across a lap when it comes back.
    const elapsed = Math.min(now - previous, 100);
    const step = this.speed * (elapsed / 1000);
    if (step > 0) this.scrollLeft += step;
  };
}

if (!customElements.get('rd-builds-marquee')) {
  customElements.define('rd-builds-marquee', RdBuildsMarquee);
}
